
import { useCallback } from 'react';
import useMetaTracking from './use-meta-tracking';

// Event categories
export type EventCategory = 
  | 'engagement'
  | 'conversion'
  | 'lead'
  | 'navigation'
  | 'form'
  | 'calculator'
  | 'video'
  | 'external_link'
  | 'plan_selection'
  | 'portal_access';

// Event parameters interface
export interface AnalyticsEventParams {
  [key: string]: string | number | boolean | undefined;
}

export const useAnalytics = () => {
  const metaTracking = useMetaTracking();
  
  // Generic event tracker
  const trackEvent = useCallback((
    eventName: string,
    category: EventCategory,
    params: AnalyticsEventParams = {}
  ) => {
    // Track with Google Analytics
    if (window.gtag) {
      window.gtag('event', eventName, {
        event_category: category,
        ...params
      });
      console.log(`[Analytics] Event tracked: ${eventName}`, { category, ...params });
    }
    
    // Also track with Meta Pixel for key events
    if (['lead', 'conversion', 'form'].includes(category)) {
      // Map to appropriate Meta event
      const metaEventMapping: Record<string, any> = {
        'form_submit': 'Lead',
        'registration_complete': 'CompleteRegistration',
        'contact_submit': 'Contact',
        'schedule_appointment': 'Schedule',
        'plan_selected': 'StartTrial',
        'application_submit': 'SubmitApplication',
        'content_view': 'ViewContent'
      };
      
      const metaEvent = metaEventMapping[eventName] || 'CustomEvent';
      metaTracking.trackEvent(metaEvent, params);
    }
  }, [metaTracking]);
  
  // Page view tracking
  const trackPageView = useCallback((pageName: string, pageCategory: string) => {
    metaTracking.trackViewContent({
      content_name: pageName,
      content_category: pageCategory
    });
  }, [metaTracking]);
  
  // Specific event trackers
  const trackButtonClick = useCallback((buttonName: string, buttonLocation: string) => {
    trackEvent('button_click', 'engagement', {
      button_name: buttonName,
      button_location: buttonLocation
    });
  }, [trackEvent]);
  
  const trackFormStart = useCallback((formName: string) => {
    trackEvent('form_start', 'form', {
      form_name: formName
    });
  }, [trackEvent]);
  
  const trackFormSubmit = useCallback((formName: string, formSuccess: boolean = true) => {
    trackEvent('form_submit', 'form', {
      form_name: formName,
      form_success: formSuccess
    });
  }, [trackEvent]);
  
  const trackCalculatorUse = useCallback((calculatorType: string, inputValues: object) => {
    trackEvent('calculator_use', 'calculator', {
      calculator_type: calculatorType,
      input_values: JSON.stringify(inputValues)
    });
  }, [trackEvent]);
  
  const trackVideoEngagement = useCallback((videoName: string, action: 'play' | 'pause' | '25%' | '50%' | '75%' | 'complete') => {
    trackEvent('video_' + action, 'video', {
      video_name: videoName
    });
  }, [trackEvent]);
  
  const trackPlanSelection = useCallback((planName: string, planPrice?: number) => {
    trackEvent('plan_selected', 'conversion', {
      plan_name: planName,
      plan_price: planPrice
    });
  }, [trackEvent]);
  
  const trackPortalAccess = useCallback((portalName: string) => {
    trackEvent('portal_access', 'portal_access', {
      portal_name: portalName
    });
  }, [trackEvent]);
  
  const trackExternalLink = useCallback((linkUrl: string, linkText: string) => {
    trackEvent('external_link_click', 'external_link', {
      link_url: linkUrl,
      link_text: linkText
    });
  }, [trackEvent]);
  
  const trackRegistrationComplete = useCallback((registrationType: string) => {
    trackEvent('registration_complete', 'conversion', {
      registration_type: registrationType
    });
  }, [trackEvent]);
  
  return {
    trackEvent,
    trackPageView,
    trackButtonClick,
    trackFormStart, 
    trackFormSubmit,
    trackCalculatorUse,
    trackVideoEngagement,
    trackPlanSelection,
    trackPortalAccess,
    trackExternalLink,
    trackRegistrationComplete
  };
};

export default useAnalytics;
